import { useEffect, useState } from "react";
import { ApiError } from "../../lib/api";
import { runActive, runsApi, type RunEvent, type RunInfo } from "../../lib/productsApi";

const EVERY_MS = 2500;
const PAGE = 200;

/**
 * A run, watched live: its row and its log, polled every few seconds while the agent has it. The run is
 * read before its events, so once it reads as ended the events fetched after it are complete.
 */
export function useRun(runId: string | null) {
  const [run, setRun] = useState<RunInfo | null>(null);
  const [events, setEvents] = useState<RunEvent[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRun(null);
    setEvents([]);
    setError(null);
    if (!runId) return;
    let stopped = false;
    let after = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      try {
        const { run: r } = await runsApi.get(runId);
        const { events: page } = await runsApi.events(runId, after);
        if (stopped) return;
        if (page.length) {
          after = page[page.length - 1].seq;
          setEvents((prev) => [...prev, ...page]);
        }
        setRun(r);
        setError(null);
        if (page.length === PAGE) timer = setTimeout(tick, 0);
        else if (runActive(r.state)) timer = setTimeout(tick, EVERY_MS);
      } catch (e) {
        if (stopped) return;
        setError(e instanceof ApiError ? e.message : "Could not reach Flashadz.");
        timer = setTimeout(tick, EVERY_MS * 2);
      }
    };
    void tick();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [runId]);

  return { run, events, error };
}
